<template>
  <div
    ref="marketCarpetElement"
    class="market-carpet-element"
    @mouseleave="clearHoverData()"
  >
    <ControlBar v-if="showControlBar" :show-share-button="syncUrl" />
    <div class="carpet-area">
      <div class="carpet-column">
        <div ref="carpetContainer" class="carpet-container">
          <MarketCarpet
            v-if="carpetContainerWidth > 0 && carpetContainerHeight > 0"
            :show-symbols="showSymbols"
            :width="carpetContainerWidth"
            :height="carpetContainerHeight"
          />
          <HoverBox
            :hover-data="hoverData"
            :carpet-width="carpetContainerWidth"
            :carpet-height="carpetContainerHeight"
            :top-offset="hoverBoxTopOffset"
          />
        </div>
        <div v-if="showColorScale" class="lower-bar">
          <p class="hint">
            <i class="far fa-fw fa-lightbulb" aria-hidden="true"></i>
            Double click on a box to open the Symbol Summary for that ticker
          </p>
          <div class="legend-container">
            <div class="timestamp">{{ dateRange }} {{ formattedTimestamp }}</div>
            <ColorScale :height="30" :box-width="50" :box-count="colorScaleBoxCount" />
          </div>
        </div>
      </div>
      <div v-if="isSideTableVisible" class="side-table-container">
        <SideTable />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
  import MarketCarpet from '@/components/MarketCarpet.vue';
  import HoverBox from '@/components/HoverBox.vue';
  import ControlBar from '@/components/ControlBar.vue';
  import SideTable from '@/components/SideTable.vue';
  import ColorScale from '@/components/ColorScale.vue';
  import { createMarketCarpetContext, provideMarketCarpetContext } from '@/ts/marketCarpetContext';
  import { isValidPeriod, DEFAULT_PERIOD } from '@/ts/composables/usePeriod';
  import { isValidMeasurement, DEFAULT_CALCULATOR } from '@/ts/composables/useCalculator';
  import { isValidColorScheme, DEFAULT_COLOR_SCHEME } from '@/ts/composables/useColorScheme';
  import { isValidSizeBy, DEFAULT_SIZE_BY } from '@/ts/composables/useSizeBy';
  import { DEFAULT_SHOW_SYMBOLS } from '@/ts/composables/useShowSymbols';
  import { DEFAULT_AUTO_REFRESH } from '@/ts/composables/useMarketCarpetData';
  import { DEFAULT_IS_HEADER_SHOWN, DEFAULT_IS_SHARP_CHART_SHOWN } from '@/ts/composables/useHoverBox';
  import { useBoundingClientRect } from '@/ts/composables/useBoundingClientRect';
  import { AUTO_REFRESH_INTERVAL_MS } from '@/ts/constants/refresh';

  const SIDE_TABLE_WIDTH = 250;

  // Props exposed as HTML attributes.
  // Boolean props follow standard HTML boolean attribute semantics: the attribute's
  // presence means true, its absence means false. Setting auto-refresh="false" does
  // NOT disable it — the attribute is still present. To disable, omit the attribute
  // entirely, or use JS: element.removeAttribute('auto-refresh').
  const props = withDefaults(defineProps<{
    scan?: string;
    period?: string;
    measurement?: string;
    colorScheme?: string;
    sizeBy?: string;
    showSymbols?: boolean;
    autoRefresh?: boolean;
    showInfoHover?: boolean;
    showMiniChart?: boolean;
    showControlBar?: boolean;
    showSideTable?: boolean;
    showColorScale?: boolean;
    syncUrl?: boolean;
    fullscreenElementId?: string;
  }>(), {
    period: DEFAULT_PERIOD,
    measurement: DEFAULT_CALCULATOR,
    colorScheme: DEFAULT_COLOR_SCHEME,
    sizeBy: DEFAULT_SIZE_BY,
    showSymbols: DEFAULT_SHOW_SYMBOLS,
    autoRefresh: DEFAULT_AUTO_REFRESH,
    showInfoHover: DEFAULT_IS_HEADER_SHOWN,
    showMiniChart: DEFAULT_IS_SHARP_CHART_SHOWN,
    showControlBar: false,
    showSideTable: false,
    showColorScale: false,
    syncUrl: false,
  });

  const marketCarpetElement = ref<HTMLElement>();
  const carpetContainer = ref<HTMLElement>();

  function parseValidated<T extends string>(value: string, isValid: (v: string) => v is T, fallback: T): T {
    return isValid(value) ? value : fallback;
  }

  const marketCarpetContext = createMarketCarpetContext({
    scan: props.scan,
    period: parseValidated(props.period, isValidPeriod, DEFAULT_PERIOD),
    calculator: parseValidated(props.measurement, isValidMeasurement, DEFAULT_CALCULATOR),
    colorScheme: parseValidated(props.colorScheme, isValidColorScheme, DEFAULT_COLOR_SCHEME),
    sizeBy: parseValidated(props.sizeBy, isValidSizeBy, DEFAULT_SIZE_BY),
    showSymbols: props.showSymbols,
    autoRefresh: props.autoRefresh,
    showInfoHover: props.showInfoHover,
    showMiniChart: props.showMiniChart,
    showSideTable: props.showSideTable,
    syncUrl: props.syncUrl
  });

  provideMarketCarpetContext(marketCarpetContext);
  const { showSymbols, hoverData, clearHoverData, dateRange, formattedTimestamp } = marketCarpetContext;

  const { width: marketCarpetElementWidth } = useBoundingClientRect(marketCarpetElement, { windowResize: true });
  const { width: carpetContainerWidth, height: carpetContainerHeight, y: carpetContainerY } = useBoundingClientRect(carpetContainer, { windowResize: true, windowScroll: true });

  const isSideTableVisible = computed(() => marketCarpetContext.showSideTable.value && marketCarpetElementWidth.value > 800);

  const hoverBoxTopOffset = computed(() => {
    if (!hoverData.value)
      return undefined;

    return hoverData.value.mouseEvent.clientY - carpetContainerY.value;
  });

  const colorScaleBoxCount = computed(() => {
    if (carpetContainerWidth.value < 400)
      return 3;

    if (carpetContainerWidth.value < 800)
      return 5;

    return 9;
  });

  let refreshInterval: number | undefined;

  const FONT_AWESOME_CSS_HREF = '//d.stockcharts.com/font-awesome/css/all-optimized.css';

  function injectStylesheetIntoShadow(shadowRoot: ShadowRoot, href: string) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    shadowRoot.prepend(link);
  }

  function updateFullscreenTarget(elementId: string | undefined) {
    const externalTarget = elementId ? document.getElementById(elementId) : null;
    if (externalTarget) {
      marketCarpetContext.fullscreenTarget.value = externalTarget;
      return;
    }
    const root = marketCarpetElement.value?.getRootNode();
    if (root instanceof ShadowRoot && root.host instanceof HTMLElement) {
      marketCarpetContext.fullscreenTarget.value = root.host;
    } else if (marketCarpetElement.value) {
      marketCarpetContext.fullscreenTarget.value = marketCarpetElement.value;
    }
  }

  onMounted(() => {
    const root = marketCarpetElement.value?.getRootNode();

    if (root instanceof ShadowRoot) {
      // FA CSS class rules (.fas, .far, .fa-expand, etc.) must be in the
      // Shadow DOM because CSS classes don't cross shadow boundaries. The
      // host page is responsible for loading the actual font files.
      injectStylesheetIntoShadow(root, FONT_AWESOME_CSS_HREF);
    }

    updateFullscreenTarget(props.fullscreenElementId);

    if (marketCarpetContext.scan.value) {
      marketCarpetContext.fetchData(marketCarpetContext.scan.value);
    }

    // Interval runs unconditionally so that dynamic toggling of the autoRefresh
    // attribute works correctly (the old conditional-creation approach silently
    // ignored later attribute changes). The cost is one boolean check per 60s.
    refreshInterval = globalThis.setInterval(() => {
      if (marketCarpetContext.isAutoRefreshEnabled.value && marketCarpetContext.scan.value) {
        marketCarpetContext.fetchData(marketCarpetContext.scan.value, false);
      }
    }, AUTO_REFRESH_INTERVAL_MS);
  });

  onUnmounted(() => {
    // The truthiness check is safe here: globalThis.setInterval always returns a
    // positive integer per the HTML spec, so 0 is never a valid handle. This
    // guard only distinguishes "interval was created" from the initial undefined.
    if (refreshInterval) {
      clearInterval(refreshInterval);
      refreshInterval = undefined;
    }
  });

  // Watchers sync external HTML attribute changes into the context. Only props
  // that have a corresponding writable ref in the context need watchers here.
  // Props like showControlBar and showColorScale are consumed directly from
  // props in the template (v-if) and are never written to by child components,
  // so they intentionally have no watcher and no context ref.
  watch(() => props.scan, (newScan) => {
    if (newScan && newScan !== marketCarpetContext.scan.value) {
      marketCarpetContext.scan.value = newScan;
      marketCarpetContext.fetchData(newScan);
    }
  });

  watch(() => props.period, (newPeriod) => {
    const parsed = parseValidated(newPeriod, isValidPeriod, DEFAULT_PERIOD);
    if (parsed !== marketCarpetContext.periodKey.value) {
      marketCarpetContext.periodKey.value = parsed;
    }
  });

  watch(() => props.measurement, (newMeasurement) => {
    const parsed = parseValidated(newMeasurement, isValidMeasurement, DEFAULT_CALCULATOR);
    if (parsed !== marketCarpetContext.calculatorKey.value) {
      marketCarpetContext.calculatorKey.value = parsed;
    }
  });

  watch(() => props.colorScheme, (newColorScheme) => {
    const parsed = parseValidated(newColorScheme, isValidColorScheme, DEFAULT_COLOR_SCHEME);
    if (parsed !== marketCarpetContext.colorSchemeKey.value) {
      marketCarpetContext.colorSchemeKey.value = parsed;
    }
  });

  watch(() => props.sizeBy, (newSizeBy) => {
    const parsed = parseValidated(newSizeBy, isValidSizeBy, DEFAULT_SIZE_BY);
    if (parsed !== marketCarpetContext.sizeByKey.value) {
      marketCarpetContext.sizeByKey.value = parsed;
    }
  });

  watch(() => props.showSymbols, (newShowSymbols) => {
    if (newShowSymbols !== marketCarpetContext.showSymbols.value) {
      marketCarpetContext.showSymbols.value = newShowSymbols;
    }
  });

  watch(() => props.autoRefresh, (newAutoRefresh) => {
    if (newAutoRefresh !== marketCarpetContext.isAutoRefreshEnabled.value) {
      marketCarpetContext.isAutoRefreshEnabled.value = newAutoRefresh;
    }
  });

  watch(() => props.showInfoHover, (newShowInfoHover) => {
    if (newShowInfoHover !== marketCarpetContext.isHeaderShown.value) {
      marketCarpetContext.isHeaderShown.value = newShowInfoHover;
    }
  });

  watch(() => props.showMiniChart, (newShowMiniChart) => {
    if (newShowMiniChart !== marketCarpetContext.isSharpChartShown.value) {
      marketCarpetContext.isSharpChartShown.value = newShowMiniChart;
    }
  });

  watch(() => props.showSideTable, (newShowSideTable) => {
    if (newShowSideTable !== marketCarpetContext.showSideTable.value) {
      marketCarpetContext.setShowSideTable(newShowSideTable);
    }
  });

  watch(() => props.fullscreenElementId, (newElementId) => {
    updateFullscreenTarget(newElementId);
  });
</script>

<style>
  /* Custom elements default to display: inline, which has no definite height.
     Without this rule, the inner div's height: 100% resolves to 0 and the
     ResizeObserver never reports a usable size. */
  :host {
    display: block;
    width: 100%;
    height: 100%;

    --white-color: #ffffff;
    --component-white-color: #f2f5f7;
    --green-color: #219155;
    --up-color: #1e824c;
    --down-color: #cc000e;
    --border-color: #c0ced8;
    --background-color: #707f8f;
  }

  :host(:fullscreen) {
    background-color: var(--white-color);
  }

  .market-carpet-element {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    overflow: hidden;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .carpet-area {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    gap: 5px;
    padding: 5px;
  }

  .carpet-column {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
    gap: 5px;
  }

  .carpet-container {
    flex: 1;
    position: relative;
    min-width: 0;
    min-height: 0;
  }

  .side-table-container {
    width: v-bind('`${SIDE_TABLE_WIDTH}px`');
    background-color: var(--component-white-color);
    border: 1px solid var(--border-color);
    overflow: hidden;
    flex-shrink: 0;
  }

  .lower-bar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex: 0 0 40px;
  }

  .hint {
    margin: 5px 0;
    padding: 0 5px;
    font-size: 14px;
    font-weight: normal;
    color: #5a6672;
  }

  .legend-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
  }

  .timestamp {
    font-size: 14px;
    font-weight: bold;
    color: #5a6672;
    white-space: nowrap;
  }

  @media (max-width: 991px) {
    .hint {
      display: none;
    }
  }

  @media (max-width: 576px) {
    .timestamp {
      font-size: 12px;
    }
  }
</style>
